import React from 'react';
import { Card, Text } from '../ui';

/**
 * A single document type card showing title, count, and add button.
 * Mirrors iOS DocumentTypeSectionView. No `onAdd` (a view-only user) → no
 * add button.
 */
export default function DocumentTypeCard({
  config,
  count,
  onAdd,
  onView,
  disabled,
  disabledReason,
}) {
  const countLabel =
    count === 0
      ? 'No documents'
      : count === 1
        ? '1 document'
        : `${count} documents`;

  const clickable = !disabled && count > 0;

  return (
    <Card
      variant="glass"
      padding="md"
      interactive={clickable}
      className={`doc-type-card ${disabled ? 'doc-type-card--disabled' : ''}`}
      onClick={clickable ? onView : undefined}
      style={{ '--card-accent': config.color }}
      data-doc-type={config.key}
      data-testid={`doc-type-card-${config.key}`}
    >
      <div className="doc-type-card__header">
        <div
          className="doc-type-card__icon"
          style={{ backgroundColor: config.color + '20', color: config.color }}
        >
          <DocTypeIcon typeKey={config.key} />
        </div>
        <div className="doc-type-card__info">
          <Text
            as="h3"
            tone="dark"
            size="base"
            className="doc-type-card__title"
          >
            {config.title}
          </Text>
          <Text tone="dark" variant="subtle" size="sm">
            {countLabel}
          </Text>
        </div>
        {onAdd && (
          <button
            type="button"
            className="doc-type-card__add-btn"
            data-testid={`doc-type-add-${config.key}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onAdd();
            }}
            disabled={disabled}
            title={disabled ? disabledReason : `Create ${config.title}`}
          >
            +
          </button>
        )}
      </div>
      {config.previewNote && (
        <Text
          tone="dark"
          variant="subtle"
          size="xs"
          className="doc-type-card__note"
        >
          {config.previewNote}
        </Text>
      )}
      {disabled && disabledReason && (
        <Text size="xs" className="doc-type-card__disabled-reason">
          {disabledReason}
        </Text>
      )}
    </Card>
  );
}

function DocTypeIcon({ typeKey }) {
  const icons = {
    parcelMap: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
      </svg>
    ),
    vicinityMap: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
      </svg>
    ),
    codeSpecificText: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
      </svg>
    ),
    schedules: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M3 3v18h18V3H3zm8 16H5v-6h6v6zm0-8H5V5h6v6zm8 8h-6v-6h6v6zm0-8h-6V5h6v6z" />
      </svg>
    ),
    coverRendering: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M12 10.8c-1.32 0-2.4 1.08-2.4 2.4s1.08 2.4 2.4 2.4 2.4-1.08 2.4-2.4-1.08-2.4-2.4-2.4zm8-3.6H16.8l-1.68-1.8H8.88L7.2 7.2H4c-1.1 0-2 .9-2 2v10.8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9.2c0-1.1-.9-2-2-2zm-8 12c-2.64 0-4.8-2.16-4.8-4.8s2.16-4.8 4.8-4.8 4.8 2.16 4.8 4.8-2.16 4.8-4.8 4.8z" />
      </svg>
    ),
    overallFloorplan: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M11 21H5c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h6v18zm2 0h6c1.1 0 2-.9 2-2v-7h-8v9zm8-11V5c0-1.1-.9-2-2-2h-6v7h8z" />
      </svg>
    ),
    roomPackage: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" />
      </svg>
    ),
    proposal: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm1 10h-4v1h3c.55 0 1 .45 1 1v3c0 .55-.45 1-1 1h-1v1h-2v-1H9v-2h4v-1h-3c-.55 0-1-.45-1-1v-3c0-.55.45-1 1-1h1V8h2v1h2v2zm-2-4V3.5L17.5 8H13z" />
      </svg>
    ),
    fullCD: (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 9h-2v2H9v-2H7V9h2V7h2v2h2v2zm-2-7l5.5 5.5H11V4z" />
      </svg>
    ),
  };

  return icons[typeKey] || null;
}
